<script setup lang="ts">
import { computed } from 'vue';
import { pvpQueue, cancelQueue, acceptQueue } from '../game/pvp-queue';
import { confirmAction } from '../game/confirm';
import { t } from '../i18n';

/**
 * THE QUEUE DOCK — the Holmgang queue riding along in the corner while the
 * player is anywhere but the Pit (CU-86cb9yd3p follow-up: "like the little
 * corner panel in Dota — the search must not stop when you go to another
 * page"). Pure view over `pvp-queue.ts`; it owns no state and no timers.
 *
 * Searching shows the honest elapsed count and a free walk-out. A found foe
 * shows who, the accept window counting down (server's own deadline), FIGHT,
 * and a cancel that is PRICED — dropping a revealed foe costs the fee, so the
 * dock asks through the same fee-worded dialog the Pit uses before charging.
 */
const emit = defineEmits<{
  /** FIGHT accepted — the shell routes to the Pit, whose mount-time resume
   *  picks up the live session (it never starts a second one). */
  (e: 'fight'): void;
  /** Money moved (a paid cancel) — the shell refreshes the header. */
  (e: 'updated'): void;
}>();

/** The city map parks its minimap in this same corner (absolute bottom-right,
 *  148px canvas) and the dock landed straight on top of it (QA 28.08 screenshot).
 *  The dock is the transient guest, so IT yields: the shell sets `lifted` on
 *  the map view and the dock rides above the minimap instead of covering it. */
defineProps<{ lifted?: boolean }>();

const searchSeconds = computed(() =>
  pvpQueue.startedAt > 0 ? Math.max(0, Math.floor((pvpQueue.now - pvpQueue.startedAt) / 1000)) : 0);

const holdLeft = computed(() => {
  const total = Math.max(0, Math.ceil((pvpQueue.expiresAt - pvpQueue.now) / 1000));
  return { m: Math.floor(total / 60), s: String(total % 60).padStart(2, '0'), total };
});
const holdUrgent = computed(() => holdLeft.value.total > 0 && holdLeft.value.total <= 60);

async function fight() {
  if (pvpQueue.busy) return;
  try {
    await acceptQueue();
    emit('fight');
  } catch { /* refused — the store carries the message into the Pit's lobby */ }
}

/** Dropping a REVEALED foe from the dock costs rating — same dialog the
 *  shell's old leave-path used, so the price is named before the click
 *  wherever the cancel happens. Mid-search stays a free, dialog-less out. */
async function cancel() {
  if (pvpQueue.busy) return;
  if (pvpQueue.phase === 'found' && pvpQueue.cancelRating > 0) {
    const go = await confirmAction({
      kicker: t('battle.confirm.kicker'),
      title: t('battle.confirm.dropTitle'),
      note: t('battle.confirm.dropNoteRating', { rating: pvpQueue.cancelRating }),
      confirmLabel: t('battle.confirm.dropConfirmRating', { rating: pvpQueue.cancelRating }),
      cancelLabel: t('battle.confirm.stay'),
      danger: false,
    });
    if (!go) return;
  }
  const ratingLost = await cancelQueue();
  if (ratingLost > 0) emit('updated');
}
</script>

<template>
  <transition name="qd">
    <aside v-if="pvpQueue.phase !== 'idle'" class="qd bound-frame" :class="{ 'qd--lifted': lifted }" role="status" :aria-label="t('arena.title')">
      <div class="qd-kicker">{{ t('arena.title') }}</div>

      <template v-if="pvpQueue.phase === 'searching'">
        <div class="qd-row">
          <span class="qd-pulse" aria-hidden="true"></span>
          <span class="qd-txt">{{ t('arena.searching', { seconds: searchSeconds }) }}</span>
        </div>
        <button :disabled="pvpQueue.busy" class="qd-btn qd-btn--quiet" @click="cancel">{{ t('arena.leaveQueue') }}</button>
      </template>

      <template v-else>
        <div class="qd-foe">{{ t('arena.vs', { opponent: pvpQueue.opponent?.name ?? '' }) }}</div>
        <div class="qd-hold" :class="{ 'is-urgent': holdUrgent }" role="timer">
          {{ t('arena.holdLeft', { time: `${holdLeft.m}:${holdLeft.s}` }) }}
        </div>
        <div class="qd-cta">
          <button :disabled="pvpQueue.busy" class="qd-btn qd-btn--fight" @click="fight">{{ t('arena.fight') }}</button>
          <button :disabled="pvpQueue.busy" class="qd-btn qd-btn--quiet" @click="cancel">
            {{ pvpQueue.cancelRating > 0 ? t('arena.cancelWithRating', { rating: pvpQueue.cancelRating }) : t('arena.cancel') }}
          </button>
        </div>
      </template>
    </aside>
  </transition>
</template>

<style scoped>
/* Bottom-right, above content but under the full-screen overlays (fight 60,
   modals 90+). Safe-area padded so a notch/home bar never clips it. */
.qd {
  position: fixed;
  right: calc(12px + env(safe-area-inset-right, 0px));
  bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  z-index: 55;
  min-width: 208px;
  max-width: 264px;
  padding: 10px 12px;
  background: color-mix(in srgb, var(--color-surface) 92%, black);
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.45);
}
/* Clear of the map's minimap: 12px inset + 148px canvas + 2px border + 8px gap.
   Kept as one literal sum so a minimap resize is a one-line fix here. */
.qd--lifted {
  bottom: calc(170px + env(safe-area-inset-bottom, 0px));
}
.qd-kicker {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-text-dim);
  margin-bottom: 6px;
}
.qd-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.qd-txt { font-size: 12px; color: var(--color-text); }
/* The queue's heartbeat — indeterminate on purpose (a determinate bar gives
   the rolled wait away in the first second; same reasoning as the Pit's bar). */
.qd-pulse {
  width: 10px; height: 10px; border-radius: 50%;
  background: var(--color-aurora);
  animation: qd-beat 1.2s ease-in-out infinite;
}
@keyframes qd-beat { 0%, 100% { opacity: 0.35; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1); } }
@media (prefers-reduced-motion: reduce) { .qd-pulse { animation: none; opacity: 0.8; } }

.qd-foe { font-size: 13px; font-weight: 600; color: var(--color-text); margin-bottom: 2px; }
.qd-hold { font-size: 11px; letter-spacing: 0.06em; color: var(--color-text-dim); margin-bottom: 8px; }
.qd-hold.is-urgent { color: var(--color-ember); }

.qd-cta { display: flex; gap: 6px; }
.qd-btn {
  font-size: 11px;
  padding: 6px 10px;
  min-height: 32px;
  border: 1px solid color-mix(in srgb, var(--color-text-dim) 40%, transparent);
  color: var(--color-text);
  background: transparent;
  cursor: pointer;
}
.qd-btn:disabled { opacity: 0.5; cursor: default; }
.qd-btn--fight { border-color: var(--color-aurora); color: var(--color-aurora); font-weight: 700; }
.qd-btn--quiet { color: var(--color-text-dim); }

.qd-enter-active, .qd-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.qd-enter-from, .qd-leave-to { opacity: 0; transform: translateY(8px); }
</style>
